import {
  CertificationType,
  EducationType,
  SortOrder,
  WorkExperienceType,
} from "@/types";

interface ColumnSortState<K> {
  key: K;
  direction: SortOrder;
}

// Helper functions to decide sort of columns in dashboard a pipeline tables on click of a column header
// If alphabetical column, first click sorts in alphabetical (asc) order
// If numeric column, first click sorts in descending order
export const cycleColumnSort = <K>(
  prev: ColumnSortState<K> | null,
  key: K,
  isAlphaKey: (k: K) => boolean,
): ColumnSortState<K> | null => {
  const leading = isAlphaKey(key) ? SortOrder.Asc : SortOrder.Desc;
  if (prev?.key !== key) return { key, direction: leading };
  if (prev.direction === leading) {
    return {
      key,
      direction: leading === SortOrder.Asc ? SortOrder.Desc : SortOrder.Asc,
    };
  }
  return null;
};

const isEffectivelyNull = (value: string | null | undefined): boolean => {
  return value === null || value === "null" || value === undefined;
};

const standardizeMonth = (month: string | null): string | null => {
  // These will all be standardized:
  // standardizeMonth('Sep') // Returns 'September'
  // standardizeMonth('sept') // Returns 'September'
  // standardizeMonth('Summer') // Returns null
  // standardizeMonth('2002') // Returns null
  // standardizeMonth('EMPTY') // Returns null
  // standardizeMonth('NULL') // Returns null (via isEffectivelyNull)
  // standardizeMonth('June') // Returns 'June' (preserved as is)
  // standardizeMonth('JuNe') // Returns 'June' (fixed capitalization)

  if (isEffectivelyNull(month)) return null;

  // Convert to string and trim whitespace.
  const normalizedMonth = String(month).trim();

  // Handle empty or numeric values.
  if (
    normalizedMonth === "" ||
    normalizedMonth === "EMPTY" ||
    /^\d+$/.test(normalizedMonth)
  ) {
    return null;
  }

  // Mapping of month variations to standard format.
  const monthMappings: Record<string, string | null> = {
    // Standard months (case-insensitive).
    jan: "January",
    feb: "February",
    mar: "March",
    apr: "April",
    may: "May",
    jun: "June",
    jul: "July",
    aug: "August",
    sep: "September",
    sept: "September",
    oct: "October",
    nov: "November",
    dec: "December",

    // Special cases.
    summer: null,
    spring: null,
    winter: null,
    fall: null,
    autumn: null,
  };

  // Convert to lowercase for matching.
  const lowercaseMonth = normalizedMonth.toLowerCase();

  // Check if it's a known variation.
  for (const [key, value] of Object.entries(monthMappings)) {
    if (lowercaseMonth.startsWith(key)) {
      return value;
    }
  }

  // If it's already a full month name, return it properly capitalized.
  const properMonths = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const properMonth =
    normalizedMonth.charAt(0).toUpperCase() +
    normalizedMonth.slice(1).toLowerCase();

  if (properMonths.includes(properMonth)) {
    return properMonth;
  }

  // If we don't recognize the format, return null.
  return null;
};

const cleanEntryDates = (
  entry: WorkExperienceType | EducationType,
  type: "workExperience" | "education",
) => {
  const cleanedEntry = { ...entry }; // Create a copy to avoid mutating the original.

  // Check if month fields contain 4-digit numeric values (likely years) - specific to education.
  if (type === "education") {
    if (cleanedEntry.startMonth && /^\d{4}$/.test(cleanedEntry.startMonth)) {
      cleanedEntry.startMonth = undefined; // Set numeric values that look like years to null.
    }

    if (cleanedEntry.endMonth && /^\d{4}$/.test(cleanedEntry.endMonth)) {
      cleanedEntry.endMonth = undefined; // Set numeric values that look like years to null.
    }
  }

  // Standardize months.
  cleanedEntry.startMonth =
    standardizeMonth(cleanedEntry.startMonth ?? null) ?? undefined;
  cleanedEntry.endMonth =
    standardizeMonth(cleanedEntry.endMonth ?? null) ?? undefined;

  // Determine if current based on type.
  const isCurrent =
    "currentPosition" in cleanedEntry && cleanedEntry.currentPosition === true;

  if (isCurrent) {
    // Set end dates to null for current entries.
    cleanedEntry.endMonth = undefined;
    cleanedEntry.endYear = undefined;

    // Handle start dates for current entries.
    if (
      isEffectivelyNull(cleanedEntry.startMonth) &&
      isEffectivelyNull(cleanedEntry.startYear)
    ) {
      // Both start month and year missing.
      cleanedEntry.startMonth = undefined;
      cleanedEntry.startYear = undefined;
    } else if (
      !isEffectivelyNull(cleanedEntry.startMonth) &&
      isEffectivelyNull(cleanedEntry.startYear)
    ) {
      // Has month but no year - mark both as Unspecified
      cleanedEntry.startMonth = "Unspecified";
      cleanedEntry.startYear = "Unspecified";
    } else if (
      isEffectivelyNull(cleanedEntry.startMonth) &&
      !isEffectivelyNull(cleanedEntry.startYear)
    ) {
      // Has year but no month - only mark month as null.
      cleanedEntry.startMonth = undefined;
    }
  } else {
    // Handle non-current entries.
    // First check if both dates are completely missing.
    if (
      isEffectivelyNull(cleanedEntry.startMonth) &&
      isEffectivelyNull(cleanedEntry.startYear) &&
      isEffectivelyNull(cleanedEntry.endMonth) &&
      isEffectivelyNull(cleanedEntry.endYear)
    ) {
      cleanedEntry.startMonth = undefined;
      cleanedEntry.startYear = undefined;
      cleanedEntry.endMonth = undefined;
      cleanedEntry.endYear = undefined;
    } else {
      // Handle partial date cases.
      // Check start date: if has month but no year.
      if (
        !isEffectivelyNull(cleanedEntry.startMonth) &&
        isEffectivelyNull(cleanedEntry.startYear)
      ) {
        cleanedEntry.startMonth = "Unspecified";
        cleanedEntry.startYear = "Unspecified";
      }
      // Check start date: if has year but no month.
      else if (
        isEffectivelyNull(cleanedEntry.startMonth) &&
        !isEffectivelyNull(cleanedEntry.startYear)
      ) {
        cleanedEntry.startMonth = undefined;
      }

      // Check end date: if has month but no year.
      if (
        !isEffectivelyNull(cleanedEntry.endMonth) &&
        isEffectivelyNull(cleanedEntry.endYear)
      ) {
        cleanedEntry.endMonth = "Unspecified";
        cleanedEntry.endYear = "Unspecified";
      }
      // Check end date: if has year but no month.
      else if (
        isEffectivelyNull(cleanedEntry.endMonth) &&
        !isEffectivelyNull(cleanedEntry.endYear)
      ) {
        cleanedEntry.endMonth = undefined;
      }
    }
  }

  return cleanedEntry;
};

const getDateValue = (year: string | null, month: string | null): number => {
  if (isEffectivelyNull(year) || year === "Unspecified") return -1;

  const yearNum = year ? parseInt(year) : 0;
  const monthNum = getMonthNumber(month) ?? 12; // Default to December if month is null.

  return yearNum * 12 + monthNum;
};

// Helper function to convert month string to number for comparison.
const getMonthNumber = (month: string | null): number | null => {
  if (isEffectivelyNull(month) || month === "Unspecified") return null;

  // If month is a number between 1-12 or 01-12, return it directly.
  if (month && /^(0?[1-9]|1[0-2])$/.test(month)) {
    return parseInt(month);
  }

  const months: Record<string, number> = {
    January: 1,
    February: 2,
    March: 3,
    April: 4,
    May: 5,
    June: 6,
    July: 7,
    August: 8,
    September: 9,
    October: 10,
    November: 11,
    December: 12,
  };
  return month ? months[month] || null : null;
};

const sortExperiences = (
  workExperiences: WorkExperienceType[] | EducationType[],
  type: "workExperience" | "education",
) => {
  const cleanedEntries = workExperiences.map((entry) =>
    cleanEntryDates(entry, type),
  );

  return cleanedEntries.sort((a, b) => {
    // Get the correct property name for checking current status.
    const currentProp =
      type === "workExperience" ? "currentPosition" : "current";

    // Current entries should always be at the top.
    if (a[currentProp as keyof typeof a] && !b[currentProp as keyof typeof b])
      return -1;
    if (!a[currentProp as keyof typeof a] && b[currentProp as keyof typeof b])
      return 1;
    if (a[currentProp as keyof typeof a] && b[currentProp as keyof typeof b]) {
      // If both are current entries, compare by start date.
      const aStartValue = getDateValue(
        a.startYear ?? null,
        a.startMonth ?? null,
      );
      const bStartValue = getDateValue(
        b.startYear ?? null,
        b.startMonth ?? null,
      );
      return bStartValue - aStartValue; // Most recent first.
    }

    // For non-current entries.
    const aEndValue = getDateValue(a.endYear ?? null, a.endMonth ?? null);
    const bEndValue = getDateValue(b.endYear ?? null, b.endMonth ?? null);

    // If both end dates are unavailable or both end dates are the same, compare start dates.
    if (aEndValue === bEndValue) {
      const aStartValue = getDateValue(
        a.startYear ?? null,
        a.startMonth ?? null,
      );
      const bStartValue = getDateValue(
        b.startYear ?? null,
        b.startMonth ?? null,
      );

      // If both start dates are also unavailable or the same, move both to bottom.
      if (aStartValue === bStartValue) return 0;
      if (aStartValue === -1) return 1; // a goes to bottom.
      if (bStartValue === -1) return -1; // b goes to bottom.

      return bStartValue - aStartValue; // Most recent first.
    }

    // If one end date is unavailable, use start date for that one.
    if (aEndValue === -1) {
      const aStartValue = getDateValue(
        a.startYear ?? null,
        a.startMonth ?? null,
      );
      if (aStartValue === -1) return 1; // If no start date either, move to bottom.
      return bEndValue - aStartValue;
    }
    if (bEndValue === -1) {
      const bStartValue = getDateValue(
        b.startYear ?? null,
        b.startMonth ?? null,
      );
      if (bStartValue === -1) return -1; // If no start date either, move to bottom.
      return bStartValue - aEndValue;
    }

    // If both end dates are available, compare them.
    return bEndValue - aEndValue; // Most recent first.
  });
};

const sortCertifications = (
  certifications: CertificationType[],
): CertificationType[] => {
  return certifications.sort((a, b) => {
    const yearA = parseInt(a.startYear ?? "0") || 0;
    const yearB = parseInt(b.startYear ?? "0") || 0;
    const monthA = getMonthNumber(a.startMonth ?? null) ?? 0;
    const monthB = getMonthNumber(b.startMonth ?? null) ?? 0;
    return yearB - yearA || monthB - monthA;
  });
};

export const sort = {
  sortWorkExperiences: (
    workExperiences: WorkExperienceType[],
  ): WorkExperienceType[] => {
    const sortedWorkExperiences = sortExperiences(
      workExperiences,
      "workExperience",
    );
    return sortedWorkExperiences as WorkExperienceType[];
  },
  sortEducations: (educations: EducationType[]): EducationType[] => {
    const sortedEducations = sortExperiences(educations, "education");
    return sortedEducations as EducationType[];
  },

  sortCertifications: (
    certifications: CertificationType[],
  ): CertificationType[] => {
    return sortCertifications(certifications);
  },

  /** Newest resume first, falling back to id when a record has no createdAt. */
  sortResumeFilesDesc: <T extends { createdAt?: string; id: number }>(
    resumeFiles: T[],
  ): T[] => {
    return [...resumeFiles].sort((a, b) => {
      const createdAtA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const createdAtB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return createdAtB - createdAtA || b.id - a.id;
    });
  },
};
